// noinspection JSUnusedGlobalSymbols

import { produce } from 'immer';
import { Platform } from 'react-native';

import {
    getUserInfo as getUserInfoSelector,
    getUserInteraction
} from '../selectors';
import api from '../services/api';
import firebase from '../services/firebase';
import service from '../services/user';
import {
    asyncActionCreator,
    createReducerFor
} from './utils';

const TYPE = 'USER';

export const getDeviceSupportInfo = () => {
    const {
        constants,
        OS,
        Version,
    } = Platform;
    const getParameter = (param) => (param ? ` ${param}` : '');
    // eslint-disable-next-line no-undef
    return OS === 'web'
        ? navigator.userAgent
        : `${OS}${getParameter(constants?.Release)}${getParameter(Version)}${getParameter(
            constants?.Manufacturer,
        )}${getParameter(constants?.Model)}`;
};

const userInfoDefaults = () => ({
    userType: 'REGULAR',
    me: { name: '' },
    inDeleting: false,
    deviceInfo: getDeviceSupportInfo(),
});

export const initialState = {
    info: userInfoDefaults(),
    deviceInfo: getDeviceSupportInfo(),
    isLoading: true,
    showEmailReconfirm: true,
};

export const setShowEmailReconfirm = (value) => (dispatch) =>
    dispatch({
        type: TYPE,
        payload: { showEmailReconfirm: value },
    });

export const setUserError = (message, code) => (dispatch) =>
    dispatch({
        type: TYPE,
        payload: {
            error: message
                ? {
                    message,
                    level: 'error',
                    code,
                }
                : null,
        },
    });

export const setUserInteractionError = (error) => async (dispatch) => {
    const userInfo = await service.getUserInfo();
    dispatch({
        type: TYPE,
        payload: {
            error,
            info: {
                ...userInfo,
                error,
            },
        },
    });
};

export const signInWithPhoneNumber = asyncActionCreator(TYPE, 'signIn', false, (phone, cb) =>
    firebase.signInWithPhoneNumber(phone, cb),
);

export const sendSmsCode = asyncActionCreator(TYPE, 'signIn', false, (code, confirmation, onError) =>
    firebase.signConfirmation(confirmation, code, onError),
);

export const checkAuthenticated = asyncActionCreator(TYPE, 'isAuthenticated', undefined, () => api.checkAuth());

export const signInWithToken = asyncActionCreator(TYPE, 'signIn', false, (token, onError) =>
    firebase.signInWithToken(token, onError),
);

export const logOut = asyncActionCreator(TYPE, 'isAuthenticated', {}, () => api.logOut());

export const removeAccount = asyncActionCreator(TYPE, 'isAuthenticated', {}, () => api.removeAccount());

export const reloadUserInfo = asyncActionCreator(
    TYPE,
    'info',
    undefined,
    (onNewUserItemsFound, prefsUpdate, updateSelects) =>
        service.reloadUserInfo(onNewUserItemsFound, prefsUpdate, updateSelects),
);

export const getUserInfo = asyncActionCreator(TYPE, 'info', userInfoDefaults(), () => service.getUserInfo());

export const getUserInteractionError = () => async (dispatch, getState) => {
    const userInteraction = await getUserInteraction(getState());
    return userInteraction.error;
};

export const addCard = (data) => async (dispatch, getState) => {
    const userInfo = getUserInfoSelector(getState());
    const cards = userInfo?.me?.cards;
    cards.push(data);
    dispatch({
        type: TYPE,
        payload: { info: { ...userInfo } },
    });
};

export const startUserFetch = () => (dispatch) =>
    dispatch({
        type: TYPE,
        payload: { isFetching: true },
    });

export const stopUserFetch = () => (dispatch) =>
    dispatch({
        type: TYPE,
        payload: { isFetching: false },
    });

export const startUserLoading = () => (dispatch) =>
    dispatch({
        type: TYPE,
        payload: { isLoading: true },
    });

export const stopUserLoading = () => (dispatch) =>
    dispatch({
        type: TYPE,
        payload: { isLoading: false },
    });

export const setInDeleting = (state) => (dispatch) =>
    dispatch({
        type: TYPE,
        payload: { inDeleting: state },
    });

export const addToFamily = user => (dispatch, getState) => {
    console.log('!!!add to family store', user);
    const userState = getState?.()?.user;
    const updatedInfo = produce(userState?.info, draft => {
        if (!draft?.relatives) {
            draft.relatives = [];
        }
        draft.relatives.push(user);
        draft.me.hasFamily = !!draft?.relatives?.find?.((r) => r.isSpouse || r.isOwner);
        draft.me.numChildren = draft?.relatives?.filter?.((r) => !r.isSpouse && !r.isOwner && !r.isParent && r?.relKind !== 'OTHER').length ?? 0;
        draft.me.numParent = draft?.relatives?.filter?.((r) => r.isParent)?.length ?? 0;
        draft.me.numSpouse = draft?.relatives?.filter?.((r) => r.isSpouse)?.length ?? 0;
    });
    dispatch({
        type: TYPE,
        payload: { info: updatedInfo, isLoading: false }
    });
}

export const removeFromFamily = profileId => (dispatch, getState) => {
    const userState = getState?.()?.user;
    const updatedInfo = produce(userState?.info, draft => {
        if (draft?.relatives) {
            draft.relatives = draft.relatives.filter(
                relative => relative.profileId !== profileId
            );
        }
        draft.me.hasFamily = !!draft?.relatives?.find?.((r) => r.isSpouse || r.isOwner);
        draft.me.numChildren = draft?.relatives?.filter?.((r) => !r.isSpouse && !r.isOwner && !r.isParent && r?.relKind !== 'OTHER').length ?? 0;
        draft.me.numParent = draft?.relatives?.filter?.((r) => r.isParent)?.length ?? 0;
        draft.me.numSpouse = draft?.relatives?.filter?.((r) => r.isSpouse)?.length ?? 0;

    });
    dispatch({
        type: TYPE,
        payload: { info: updatedInfo }
    });
}


export const addCardsToProfile = (profileId, cards) => (dispatch, getState) => {
    const userInfo = getState?.()?.user?.info;
    const updatedInfo = produce(userInfo, draft => {
        const allCards = draft?.allCards ?? [];
        const allCardsToAdd = cards?.filter?.(card => !allCards?.find?.(c => c.clientId === card.clientId && c.providerCode === card.providerCode)) ?? [];
        draft.allCards = [...allCards, ...allCardsToAdd];
        if (draft?.relatives) {
            const relativeIndex = draft.relatives.findIndex(
                relative => relative.profileId === profileId
            );
            if (relativeIndex !== -1) {
                const relativesCards = draft.relatives[relativeIndex].cards ?? [];
                const cardsToAdd = cards?.filter?.(card => !relativesCards?.find?.(c => c.clientId === card.clientId && c.providerCode === card.providerCode)) ?? [];
                draft.relatives[relativeIndex].cards = [...relativesCards, ...cardsToAdd];
            }
        }
    });
    dispatch({
        type: TYPE,
        payload: { info: updatedInfo }
    });
}

export default createReducerFor(TYPE, initialState);
